// @ts-check
// Traducción bloques (modelo del editor) <-> IR-JSON (TSK-0021, DEC-012).
//
// El IR-JSON que produce este módulo refleja 1:1 los nodos y campos de
// `backend/src/app/domain/ir.py` (mismo nombre de nodo en el campo `nodo`,
// mismos nombres de campo en español) y NO contiene el `id` interno del
// editor ni ningún dato de posición/tamaño/color (ARC-R1, roadmap §7): esos
// viven exclusivamente en `editor-visual-state.js`, que este módulo NO
// importa (sin dependencia inversa, DEC-012).
//
// El modelo de bloques (`editor-model.js`) tiene la MISMA forma que la IR
// más un campo `id` en cada nodo (ver documentación de ese módulo); por eso
// la traducción es genérica: no necesita conocer los campos concretos de
// cada uno de los 20 tipos de nodo, solo distinguir "es un nodo" (`nodo` +
// `id` en el modelo) de "es un dato primitivo" (string/number/boolean/null,
// p.ej. `TipoPrimitivo`/`Operador`, que en ir.py son `Enum`, no dataclasses).
//
// Un round-trip bloques → IR-JSON → bloques conserva la LÓGICA (los mismos
// nodos, campos y valores) pero NO conserva los `id` originales: el `id` no
// es parte de la lógica del programa (ir.py no lo tiene), se regenera al
// deserializar.
//
// TSK-0068 introdujo `nombreCorto` (identificador corto opcional, campo
// transversal de `block-content-panel.js`/`canvas-view.js`, guardado como
// campo EXTRA directamente sobre el nodo del modelo) y este módulo lo
// excluía del IR-JSON con el mismo mecanismo que ya excluía `id` (sin esto,
// un bloque con nombre corto asignado se vería/editaría bien en el editor
// pero el backend, `ir_serialization.py#_construir_nodo`, que rechaza
// cualquier clave desconocida, rechazaría la EJECUCIÓN del diagrama).
//
// TSK-0213 (petición directa del usuario, 2026-07-24) elimina por completo
// el campo `nombreCorto` de `block-content-panel.js`/`canvas-view.js`: ya no
// queda NINGÚN productor de ese campo en el editor. Aun así, el filtro de
// abajo (`clave === 'nombreCorto'`) se MANTIENE a propósito, ahora puramente
// como salvaguarda defensiva: un diagrama con `nombreCorto` ya asignado
// pudo quedar guardado en el `localStorage` de un usuario real (autoguardado
// de una sesión anterior a este cambio, ver `editor-controller.js`) y, si
// ese estado se recupera y se ejecuta, el campo desconocido seguiría
// llegando hasta aquí si se retirase el filtro. Quitar el filtro sin haber
// verificado que no queda ningún otro camino de escritura de ese campo
// podría reintroducirlo por accidente; se prefiere mantenerlo, documentado
// como código muerto en la práctica pero inofensivo y barato de conservar.

import { esNodo, crearNodoGenerico } from './editor-model.js';

/**
 * @param {unknown} valor
 * @returns {valor is { nodo: string } & Record<string, unknown>}
 */
function esNodoIrJson(valor) {
  if (typeof valor !== 'object' || valor === null) return false;
  return typeof (/** @type {Record<string, unknown>} */ (valor).nodo) === 'string';
}

/**
 * Convierte un nodo del modelo del editor (con `id`) —o cualquier valor
 * anidado dentro de él (array, nodo, primitivo)— en su equivalente IR-JSON
 * puro: mismo `nodo`/campos que ir.py, sin `id` ni ningún dato visual.
 * @param {unknown} valor
 * @returns {unknown}
 */
export function serializarAIrJson(valor) {
  if (Array.isArray(valor)) {
    return valor.map(serializarAIrJson);
  }
  if (esNodo(valor)) {
    /** @type {Record<string, unknown>} */
    const json = { nodo: valor.nodo };
    for (const [clave, val] of Object.entries(valor)) {
      // `nombreCorto`: TSK-0068 lo introdujo como campo EXTRA puramente de
      // presentación del editor (identificador corto opcional), sin
      // equivalente en ningún nodo real de `backend/src/app/domain/ir.py`,
      // y TSK-0213 eliminó por completo su UI/escritura en
      // `block-content-panel.js`/`canvas-view.js` -- ya no queda ningún
      // productor de este campo. Se mantiene la exclusión (ver cabecera del
      // archivo, sección TSK-0213) SOLO como salvaguarda defensiva frente a
      // un diagrama con `nombreCorto` ya asignado que pudiera quedar en el
      // `localStorage` de una sesión previa a este cambio: si ese estado se
      // recupera y se ejecuta, el campo desconocido haría que el backend
      // (`ir_serialization.py#_construir_nodo`, que rechaza cualquier clave
      // desconocida) rechazase la ejecución.
      if (clave === 'id' || clave === 'nodo' || clave === 'nombreCorto') continue;
      json[clave] = serializarAIrJson(val);
    }
    return json;
  }
  // Primitivo: TipoPrimitivo/Operador (string), número, booleano, null.
  return valor;
}

/**
 * Reconstruye un nodo del modelo del editor (con `id` nuevo) a partir de
 * IR-JSON —o cualquier valor anidado (array, nodo, primitivo)—. Los `id`
 * generados aquí son nuevos: no cruzan la frontera IR-JSON (ir.py no tiene
 * campo `id`), por lo que dos deserializaciones del mismo IR-JSON producen
 * modelos con ids distintos pero lógica idéntica.
 * @param {unknown} json
 * @returns {unknown}
 */
export function deserializarDesdeIrJson(json) {
  if (Array.isArray(json)) {
    return json.map(deserializarDesdeIrJson);
  }
  if (esNodoIrJson(json)) {
    const { nodo, ...resto } = json;
    /** @type {Record<string, unknown>} */
    const campos = {};
    for (const [clave, val] of Object.entries(resto)) {
      campos[clave] = deserializarDesdeIrJson(val);
    }
    return crearNodoGenerico(nodo, campos);
  }
  return json;
}

/**
 * Punto de entrada esperado por el editor: serializa el `Programa` raíz del
 * modelo a IR-JSON. Lanza un error explícito si `programa` no es un nodo
 * `Programa` del modelo (validación de entrada, docs/03-frontend.md §8.1).
 * @param {unknown} programa
 * @returns {Record<string, unknown>}
 */
export function serializarPrograma(programa) {
  if (!esNodo(programa) || programa.nodo !== 'Programa') {
    throw new TypeError('serializarPrograma espera un nodo "Programa" del modelo del editor');
  }
  return /** @type {Record<string, unknown>} */ (serializarAIrJson(programa));
}

/**
 * Punto de entrada esperado por el editor: reconstruye el modelo a partir
 * de un IR-JSON cuya raíz es un nodo `Programa`. Lanza un error explícito
 * si la forma de `irJson` no es la esperada.
 * @param {unknown} irJson
 * @returns {Record<string, unknown>}
 */
export function deserializarPrograma(irJson) {
  if (!esNodoIrJson(irJson) || irJson.nodo !== 'Programa') {
    throw new TypeError('deserializarPrograma espera un IR-JSON con "nodo": "Programa"');
  }
  return /** @type {Record<string, unknown>} */ (deserializarDesdeIrJson(irJson));
}
